import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showAlert } from "@platform/ui";
import { isTossUserCancel } from "@platform/billing";
import type { CreateEducationRegistrationRequest } from "@platform/billing";
import { toUserMessage } from "@platform/api-client";
import { PATHS } from "@/shared/constants/app/routes.constant.ts";
import { useAuth } from "@/features/auth";
import { educationApi } from "../api/education.api";
import { useCreateEducationRegistration } from "../api/use-education.mutation";
import { useEducationCart } from "../model/education-cart.context";
import { requestEducationPayment } from "../lib/education-checkout";

/**
 * 접수 → (유료면) 결제창까지 한 흐름.
 *
 * 자리는 결제 승인 직전에 서버가 잡는다. 결제창이 안 뜨거나 사용자가 닫아도 접수를 지우지
 * 않는다 — 서버가 결제 미완료로 만료시키고, 그 전까지는 접수 내역에서 이어서 결제할 수 있다.
 * 여기서 롤백을 흉내내면 결제창이 이미 승인된 뒤 닫힌 경우에 접수만 사라진다.
 *
 * 그래서 실패 안내도 **접수 생성 전/후로 갈라야** 한다. 접수가 이미 만들어졌는데
 * "접수 실패" 라고 말하면 사용자는 다시 접수하고, 같은 사람이 자리를 두 개 잡는다.
 */
export function useEducationCheckout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clear } = useEducationCart();
  const createRegistration = useCreateEducationRegistration();
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (body: CreateEducationRegistrationRequest) => {
      if (!user) return;
      setSubmitting(true);

      let registrationId: string | null = null;
      try {
        const result = await createRegistration.mutateAsync(body);
        registrationId = result.registration.id;
        clear();

        // 무료 접수는 서버가 즉시 확정한다 — 결제창을 띄울 게 없다.
        if (!result.payment) {
          navigate(PATHS.educationRegistration(registrationId), {
            replace: true,
            viewTransition: true,
          });
          return;
        }

        await requestEducationPayment({
          customerKey: user.id,
          orderId: result.payment.orderId,
          orderName: result.payment.orderName,
          amount: result.payment.amount,
          registrationId,
          customerEmail: user.email,
          customerName: user.name,
        });
      } catch (e) {
        if (registrationId) {
          // 접수는 성사됐고 결제만 못 마쳤다. 자리는 결제할 때 잡히니 그 사실을 알린다.
          if (!isTossUserCancel(e))
            await showAlert({
              title: "결제를 마치지 못했습니다",
              message:
                "접수는 저장되었습니다. 자리는 결제를 마칠 때 확정되니, 접수 내역에서 이어서 결제해 주세요.",
            });
          navigate(PATHS.educationRegistration(registrationId), {
            viewTransition: true,
          });
          return;
        }
        await showAlert({
          title: "접수 실패",
          message: toUserMessage(
            e,
            "접수를 완료하지 못했습니다. 잔여 정원이 없거나 접수 기간이 지났을 수 있습니다.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [user, clear, createRegistration, navigate],
  );

  return { submit, submitting };
}

/**
 * 접수 내역에서의 결제 재개. 새 접수를 만들지 않고 기존 접수·결제를 이어 쓴다.
 *
 * 서버가 409(이미 승인됨)로 답하면 그 호출이 접수를 확정시킨 것이므로,
 * 화면을 다시 읽게만 하면 된다.
 */
export function useResumeEducationPayment(onSettled: () => void) {
  const { user } = useAuth();
  const [resuming, setResuming] = useState(false);

  const resume = useCallback(
    async (registrationId: string) => {
      if (!user) return;
      setResuming(true);
      try {
        const checkout = await educationApi.resumeRegistration(registrationId);
        await requestEducationPayment({
          customerKey: checkout.customerKey,
          orderId: checkout.orderId,
          orderName: checkout.orderName,
          amount: checkout.amount,
          registrationId,
          customerEmail: user.email,
          customerName: user.name,
        });
      } catch (e) {
        if (!isTossUserCancel(e)) onSettled();
      } finally {
        setResuming(false);
      }
    },
    [user, onSettled],
  );

  return { resume, resuming };
}
